import { useEffect, useState } from "react";
import { FileText, Download, X } from "lucide-react";
import { api, downloadBlob } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { fmtDateTime, pointUrl } from "@/lib/helpers";

export function useBlobUrl(fileId) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let href;
    let alive = true;
    if (!fileId) return undefined;
    api.get(`/files/${fileId}`, { responseType: "blob" })
      .then((r) => {
        href = URL.createObjectURL(r.data);
        if (alive) setUrl(href);
      })
      .catch(() => {});
    return () => {
      alive = false;
      if (href) URL.revokeObjectURL(href);
    };
  }, [fileId]);
  return url;
}

function Thumb({ file, onOpen }) {
  const isImg = file.content_type?.startsWith("image/");
  const url = useBlobUrl(isImg ? file.id : null);
  return (
    <button
      type="button"
      onClick={() => onOpen(file)}
      data-testid={`doc-thumb-${file.id}`}
      className="group relative aspect-square overflow-hidden rounded-lg border border-white/10 bg-black/40 transition-transform hover:-translate-y-0.5"
    >
      {isImg && url ? (
        <img src={url} alt={file.filename} className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-slate-400">
          <FileText className="h-6 w-6" />
          <span className="px-1 text-[10px]">{isImg ? "..." : "PDF"}</span>
        </div>
      )}
      <span className="absolute bottom-1 left-1 rounded bg-black/70 px-1.5 py-0.5 font-mono text-[9px] uppercase text-slate-200">
        {file.kind}
      </span>
    </button>
  );
}

function Viewer({ file, onClose }) {
  const url = useBlobUrl(file.id);
  const isImg = file.content_type?.startsWith("image/");
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl border-white/10 bg-[#111827]" data-testid="doc-viewer">
        <DialogTitle className="font-display text-xl uppercase">{file.kind} · {fmtDateTime(file.created_at)}</DialogTitle>
        {url && isImg && <img src={url} alt={file.filename} className="max-h-[65vh] w-full rounded-lg object-contain" />}
        {url && !isImg && <iframe title="pdf" src={url} className="h-[65vh] w-full rounded-lg bg-white" />}
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-400">
          <span>{file.uploaded_by_name}</span>
          {file.lat != null && (
            <a href={pointUrl(file.lat, file.lng)} target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">
              GPS {file.lat.toFixed(4)}, {file.lng.toFixed(4)}
            </a>
          )}
          <button
            type="button"
            data-testid="doc-download-btn"
            onClick={() => downloadBlob(`/files/${file.id}`, file.filename)}
            className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 px-3 py-1.5 text-white hover:bg-blue-700"
          >
            <Download className="h-3.5 w-3.5" /> Descarcă
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function FileGallery({ files, empty }) {
  const [open, setOpen] = useState(null);
  if (!files.length) return <p className="text-sm text-slate-500" data-testid="gallery-empty">{empty}</p>;
  return (
    <>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="file-gallery">
        {files.map((f) => <Thumb key={f.id} file={f} onOpen={setOpen} />)}
      </div>
      {open && <Viewer file={open} onClose={() => setOpen(null)} />}
    </>
  );
}

export const CloseIcon = X;
